<template>
  <ul class="menu-ul">
    <li v-for="(item, i) in menuItems" :key="i">
      <a
        v-if="item.value === undefined"
        href="#"
        class="menu-link"
        @click.prevent
        @click="
          () => {
            onToggleOpen(item.title);
          }
        "
      >
        <div>
          <i class="menu-link-icon" :class="[item.icon]" />
          <span>{{ item.title }}</span>
        </div>
        <i
          v-if="item.children && item.children.length > 0"
          class="menu-toggle-icon pi pi-angle-down"
        />
      </a>
      <router-link
        v-else
        :to="getRouteWithQuery(item.value!, item.params)"
        class="menu-link"
        @click="
          () => {
            item.addEvent && item.addEvent();
          }
        "
      >
        <div>
          <i class="menu-link-icon" :class="[item.icon]" />
          <span>{{ item.title }}</span>
        </div>
        <i
          v-if="item.children && item.children.length > 0"
          class="menu-toggle-icon pi pi-angle-down"
        />
      </router-link>
      <ul
        v-if="item.children && item.children.length > 0"
        class="menu-link-children"
        :class="{ opened: isMenuOpen(item.title) }"
      >
        <li v-for="(child, k) in item.children" :key="k">
          <!-- Custom slot so aria-current and the active class follow
               isChildActive: Services & Setup uses /bookings?tab=services, so
               path-only router-link matching would wrongly mark the bare
               /bookings route as current. -->
          <router-link
            v-slot="{ href, navigate }"
            :to="getRouteWithQuery(child.value!, child.params, child.query)"
            custom
          >
            <a
              :href="href"
              class="menu-link"
              :class="{ 'router-link-active': isChildActive(child) }"
              :aria-current="isChildActive(child) ? 'page' : undefined"
              @click="
                (event: MouseEvent) => {
                  child.addEvent && child.addEvent();
                  navigate(event);
                }
              "
            >
              {{ child.title }}
            </a>
          </router-link>
        </li>
      </ul>
    </li>
    <li>
      <hr class="menu-divider" />
      <a
        :href="portalHomeUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="menu-link menu-open-portal"
        @click="onOpenPortal"
      >
        <div class="menu-open-portal-body">
          <i class="menu-link-icon pi pi-external-link" />
          <span class="menu-open-portal-text">
            <span class="menu-open-portal-title">Open Portal</span>
            <span class="menu-open-portal-subtitle">See a customer's view</span>
          </span>
        </div>
      </a>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from "vue";
import { storeToRefs } from "pinia";
import { useRoute } from "vue-router";
import { useAuthStore } from "@/modules/auth/stores/auth";
import { ROUTE_NAME } from "@/shared/constants/routes";
import { addMixpanelEvent, EVENT_NAMES } from "../utils/analytics";
import { PORTAL_BASE_URL } from "@/shared/utils/env";
import { useCurrentUser } from "@/composables/useCurrentUser";
import { useImpersonationStore } from "@/modules/auth/stores/impersonation";
import { useCurrentOrganization } from "@/composables/useCurrentOrganization";
import { useWaffle } from "@/shared/composables/useWaffle";
import { prefetchChat } from "@/modules/chat/prefetchChat";

const authStore = useAuthStore();
const impersonationStore = useImpersonationStore();
const {
  isSingleOrgUser,
  isTfaEmployee,
  defaultOrg,
  hasMultipleAdminOrgs,
  canManagePayments,
} = storeToRefs(authStore);
const { fetchOrganizations, defaultOrganization } = useCurrentUser();
const waffle = useWaffle();
const isChatEnabled = computed(() => waffle.isActive("admin-chat"));
// Bulk email sends (MAI-4859…MAI-5042) roll out per organization: the feature
// mails an org's whole member list, so it stays behind a flag until an org is
// deliberately opted in.
const isEmailEnabled = computed(() => waffle.isActive("admin-email"));

// Warm the chat route's chunk (the app's largest) in the background once we
// know this org can use it, so opening Chat doesn't wait on the download.
watch(isChatEnabled, (enabled) => {
  if (enabled) prefetchChat();
});
const { currentOrganization, currentOrganizationId } = useCurrentOrganization();
const route = useRoute();

// Manual toggles let users expand/collapse groups that aren't tied to the
// active route. Being on one of a group's routes always wins (see isMenuOpen).
const manualToggles = ref<Record<string, boolean>>({});

const passThroughOnlyAllowedParams = (query: Record<string, any>) => {
  const allowedParams = ["impersonateOrg"];
  const filteredQuery: Record<string, any> = {};
  for (const key of allowedParams) {
    if (query[key] !== undefined) {
      filteredQuery[key] = query[key];
    }
  }
  return filteredQuery;
};

// Helper function to preserve query parameters during navigation
const getRouteWithQuery = (
  routeName: string,
  params?: Record<string, any>,
  query?: Record<string, any>
) => {
  return {
    name: routeName,
    params: params ?? {},
    query: { ...passThroughOnlyAllowedParams(route.query), ...(query ?? {}) },
  };
};

const onToggleOpen = (title: string) => {
  manualToggles.value[title] = !isMenuOpen(title);
};

// A child is active when its route is the current route or an ancestor of it.
// Landing routes redirect to a leaf child (e.g. /users -> user-list,
// /feed -> feed-list), so the resolved route.name is the leaf while the parent
// route name we navigate to only survives in route.matched. Matching against
// route.matched keeps those landing links highlighted (MAI-4711).
const isChildActive = (child: Omit<MenuItem, "children" | "group">) => {
  const isActiveRoute =
    route.name === child.value ||
    route.matched.some((record) => record.name === child.value);
  if (!isActiveRoute) return false;

  // "Services & Setup" points at /bookings?tab=services, so the bare /bookings
  // route (Schedule's data) must not light it up; the ?tab check disambiguates.
  if (child.value === ROUTE_NAME.BOOKINGS) {
    const activeTab = route.query.tab?.toString() ?? "bookings";
    const isScheduleChild = child.query?.tab === undefined;
    return isScheduleChild
      ? activeTab === "bookings"
      : activeTab !== "bookings";
  }
  return true;
};

// A group stays expanded whenever one of its children is active, so navigating
// to any child (including a redirecting landing route) never collapses its
// parent. Route membership therefore takes precedence over an earlier manual
// toggle; off the group's routes we honor the explicit user toggle.
const isMenuOpen = (menuTitle: string) => {
  const group = menuItems.value.find((item) => item.title === menuTitle);
  if (group?.children?.some((child) => isChildActive(child))) return true;
  return manualToggles.value[menuTitle] ?? false;
};

// Check if current organization is a network (including impersonation)
const isNetworkOrganization = computed(() => {
  const currentOrg = impersonationStore.currentOrganization;
  const orgType = currentOrg?.organization_type;
  return orgType === "network";
});

const isChatVisible = computed(
  () => isChatEnabled.value && !isNetworkOrganization.value
);

// The admin org payload already exposes the org-specific portal home URL
// (portal base + org uuid) as payment_links.booking_index_url, so reuse it
// rather than rebuilding the URL. Falls back to the configured portal base when
// the org has no payment links (e.g. payments disabled).
const portalHomeUrl = computed<string>(
  () =>
    currentOrganization.value?.payment_links?.booking_index_url ??
    PORTAL_BASE_URL
);

const onOpenPortal = () =>
  addMixpanelEvent(EVENT_NAMES.Navigation.NavAdminOpenPortal);

onMounted(async () => {
  waffle.fetchStatus();
  if (!defaultOrganization.value) await fetchOrganizations();
});

type MenuItemValue = (typeof ROUTE_NAME)[keyof typeof ROUTE_NAME] | undefined;

interface MenuItem {
  icon?: string;
  title: string;
  value?: MenuItemValue;
  params?: Record<string, any>;
  query?: Record<string, any>;
  addEvent?: () => void;
  group?: string;
  children?: Omit<MenuItem, "children" | "group">[];
}

const navEvent = (eventName: string) => () => addMixpanelEvent(eventName);

// Communication and Development groups are shared between the club and network
// navigations (MAI-4615).
const buildCommunicationGroup = (): MenuItem => ({
  icon: "pi pi-megaphone",
  title: "Communication",
  group: "admin",
  children: [
    ...(isChatVisible.value
      ? [
          {
            title: "Chat",
            value: ROUTE_NAME.CHAT_CHANNELS,
            addEvent: navEvent(EVENT_NAMES.Navigation.NavAdminChat),
          },
        ]
      : []),
    ...(isEmailEnabled.value
      ? [
          {
            title: "Email",
            // The /emails landing route (redirects to email-list) rather than
            // the leaf, so the composer at /emails/new — which shares the
            // "emails" ancestor in route.matched — keeps Email active and the
            // group expanded.
            value: ROUTE_NAME.EMAILS,
            addEvent: navEvent(EVENT_NAMES.Navigation.NavAdminEmail),
          },
        ]
      : []),
    {
      title: "Feed",
      value: ROUTE_NAME.FEED,
      addEvent: navEvent(EVENT_NAMES.Navigation.NavAdminFeed),
    },
    {
      title: "Quick Links",
      value: ROUTE_NAME.QUICK_LINKS,
      addEvent: navEvent(EVENT_NAMES.Navigation.NavAdminQuickLinks),
    },
  ],
});

const buildDevelopmentGroup = (): MenuItem => ({
  icon: "pi pi-mobile",
  title: "Development",
  group: "admin",
  children: [
    {
      title: "Training",
      value: ROUTE_NAME.TRAINING_COLLECTION_LIST,
      addEvent: navEvent(EVENT_NAMES.Navigation.NavAdminTraining),
    },
    {
      title: "Workouts",
      value: ROUTE_NAME.WORKOUT_LIST,
      addEvent: navEvent(EVENT_NAMES.Navigation.NavAdminWorkouts),
    },
    {
      title: "Exercises",
      value: ROUTE_NAME.EXERCISE_LIST,
      addEvent: navEvent(EVENT_NAMES.Navigation.NavAdminExercises),
    },
    {
      // "Data" maps to the existing Stats surface (closest current destination).
      title: "Data",
      value: ROUTE_NAME.STATS_CATEGORIES,
      addEvent: navEvent(EVENT_NAMES.Navigation.NavAdminStats),
    },
    {
      title: "Video Submissions",
      value: ROUTE_NAME.CHAT_LIST,
      addEvent: navEvent(EVENT_NAMES.Navigation.NavAdminSubmissions),
    },
  ],
});

const menuItems = computed<MenuItem[]>(() => {
  // Simplified navigation for network organizations (MAI-4615).
  if (isNetworkOrganization.value) {
    return [
      {
        icon: "pi pi-globe",
        title: "Organizations",
        value: ROUTE_NAME.NETWORK_MANAGEMENT,
        addEvent: navEvent(EVENT_NAMES.Navigation.NavAdminNetworkManagement),
      },
      {
        // Network admin goes straight to users (no Teams), so the top-level
        // item is labeled "Users" rather than "People" (MAI-4697).
        icon: "pi pi-users",
        title: "Users",
        value: ROUTE_NAME.USERS,
        addEvent: navEvent(EVENT_NAMES.Navigation.NavAdminUsers),
      },
      buildCommunicationGroup(),
      buildDevelopmentGroup(),
      {
        icon: "pi pi-cog",
        title: "Settings",
        value: ROUTE_NAME.ORGANIZATION_SETTINGS,
      },
    ];
  }

  // --- Club navigation (MAI-4615) ---
  // Teams shows for single org users or TFA employees impersonating a club.
  const shouldShowTeams =
    (isSingleOrgUser.value && defaultOrg.value?.id) ||
    (isTfaEmployee.value &&
      impersonationStore.isImpersonating &&
      currentOrganization.value?.organization_type === "club" &&
      currentOrganizationId.value);

  // Payments enabled for the current org (including impersonated org).
  const currentOrgPaymentsEnabled =
    currentOrganization.value?.payments_enabled ??
    defaultOrganization.value?.payments_enabled;

  const shouldShowSettings =
    !isTfaEmployee.value ||
    (isTfaEmployee.value &&
      impersonationStore.isImpersonating &&
      currentOrganization.value?.organization_type === "club");

  const items: MenuItem[] = [];

  // Schedule sits at the very top. Payments-enabled orgs need it for
  // bookings data; club orgs also use it for non-payments team events
  // (MAI-4805/4811), so anyone with real club-org context — same check as
  // Teams below — sees it too, payments or not (MAI-4878).
  if (currentOrgPaymentsEnabled || shouldShowTeams) {
    items.push({
      icon: "pi pi-calendar",
      title: "Schedule",
      value: ROUTE_NAME.SCHEDULE,
      addEvent: navEvent(EVENT_NAMES.Navigation.NavAdminSchedule),
    });
  }

  // People (Users always; Teams when applicable).
  const peopleChildren: Omit<MenuItem, "children" | "group">[] = [
    {
      title: "Users",
      value: ROUTE_NAME.USERS,
      addEvent: navEvent(EVENT_NAMES.Navigation.NavAdminUsers),
    },
  ];
  if (shouldShowTeams) {
    const teamsOrgId = impersonationStore.isImpersonating
      ? currentOrganizationId.value
      : defaultOrg.value?.id;
    peopleChildren.push({
      title: "Teams",
      value: ROUTE_NAME.ORGANIZATION_UPDATE,
      params: { id: teamsOrgId },
      addEvent: navEvent(EVENT_NAMES.Navigation.NavAdminTeams),
    });
  }
  items.push({
    icon: "pi pi-users",
    title: "People",
    group: "admin",
    children: peopleChildren,
  });

  // Organizations switcher for multi-org users who don't get a Teams entry.
  if (!isSingleOrgUser.value && !shouldShowTeams) {
    items.push({
      icon: "pi pi-building",
      title: "Organizations",
      value: isTfaEmployee.value
        ? ROUTE_NAME.NETWORK_MANAGEMENT
        : ROUTE_NAME.ORGANIZATION_LIST,
      addEvent: () => {
        if (isTfaEmployee.value) {
          addMixpanelEvent(EVENT_NAMES.Navigation.NavAdminNetworkManagement);
        }
      },
    });
  }

  // Booking, Memberships, Registrations, Finance: payments-enabled org AND the
  // payments tier — basic admins (MAI-4956) keep Schedule but not these.
  if (currentOrgPaymentsEnabled && canManagePayments.value) {
    items.push({
      icon: "pi pi-clock",
      title: "Booking",
      group: "admin",
      children: [
        {
          title: "Booked Sessions",
          value: ROUTE_NAME.BOOKED_SESSIONS,
          addEvent: navEvent(EVENT_NAMES.Navigation.NavAdminBookedSessions),
        },
        {
          title: "Services & Setup",
          value: ROUTE_NAME.BOOKINGS,
          query: { tab: "services" },
        },
      ],
    });
    items.push({
      icon: "pi pi-sync",
      title: "Memberships",
      value: ROUTE_NAME.PAYMENT_MEMBERSHIPS,
      addEvent: navEvent(EVENT_NAMES.Navigation.NavAdminMemberships),
    });
    items.push({
      icon: "pi pi-tags",
      title: "Registrations",
      value: ROUTE_NAME.PAYMENT_REGISTRATIONS,
      addEvent: navEvent(EVENT_NAMES.Navigation.NavAdminRegistrations),
    });
    items.push({
      icon: "pi pi-dollar",
      title: "Finance",
      group: "admin",
      children: [
        {
          title: "Payments Log",
          value: ROUTE_NAME.PAYMENT_LOG,
        },
        {
          title: "Invoices",
          value: ROUTE_NAME.PAYMENT_INVOICES,
        },
      ],
    });
  }

  // Communication and Development always show.
  items.push(buildCommunicationGroup());
  items.push(buildDevelopmentGroup());

  if (shouldShowSettings) {
    items.push({
      icon: "pi pi-cog",
      title: "Settings",
      value: ROUTE_NAME.ORGANIZATION_SETTINGS,
    });
  }

  // Org admins with more than one admin profile need a way back to the picker.
  // (TFA employees return above and switch orgs via Network Management instead.)
  if (hasMultipleAdminOrgs.value) {
    items.push({
      icon: "pi pi-sync",
      title: "Switch organization",
      value: ROUTE_NAME.SELECT_ORGANIZATION,
    });
  }

  // Administration is TFA-employee-only.
  if (isTfaEmployee.value) {
    items.push({
      icon: "pi pi-box",
      title: "Administration",
      group: "admin",
      children: [
        {
          icon: "pi pi-database",
          title: "Global settings",
          value: ROUTE_NAME.GLOBAL_SETTINGS,
        },
      ],
    });
  }

  return items;
});
</script>

<style scoped>
.menu-ul {
  list-style: none;
  padding: 0;
}

.menu-link-children {
  list-style: none;
  padding: 0 0 0 12px;
  margin: 0 0 0 22px;
  border-left: 1px solid var(--surface-200);
  display: none;
}

.menu-link-children.opened {
  display: block;
}

.menu-link-children a {
  font-weight: normal;
}

.menu-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  font-size: 0.8rem;
  color: black;
  padding: 8px 10px;
  border-radius: 6px;
  text-decoration: none;
  border: 1px solid transparent;
  margin-bottom: 5px;
}

.menu-link i.menu-link-icon {
  color: black;
  font-size: 14px;
  border: 1px solid var(--surface-200);
  border-radius: 4px;
  padding: 4px;
  margin-right: 10px;
}

.menu-link i.menu-toggle-icon {
  color: #333;
  font-size: 0.8rem;
}

.menu-link:hover {
  background: var(--surface-50);
}

.menu-link:focus {
  border: 1px solid var(--primary-500);
}

.menu-link.router-link-active {
  background: var(--highlight-bg);
  color: var(--primary-700);
}

.menu-link.router-link-active i {
  color: var(--primary-700);
}

.menu-divider {
  border: none;
  border-top: 1px solid var(--surface-200);
  margin: 20px 0;
}

.menu-link.menu-open-portal {
  background: var(--surface-50);
  color: var(--p-primary-color);
  align-items: center;
}

.menu-open-portal-body {
  display: flex;
  align-items: center;
}

.menu-open-portal-text {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.menu-open-portal-title {
  font-weight: 600;
}

.menu-open-portal-subtitle {
  font-weight: 400;
  font-size: 0.7rem;
  color: var(--text-color-secondary);
}

.menu-link.menu-open-portal i.menu-link-icon {
  color: var(--p-primary-color);
  border: none;
  padding: 0;
}

.menu-link.menu-open-portal:hover {
  background: var(--surface-100);
}
</style>
